import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, Link2Off, Loader2 } from "lucide-react";
import betaAPI from "v2/api";
import { dateLocale } from "i18n";
import V2AuthButton from "v2/views/auth/components/V2AuthButton";
import ConsentChoice, { RequiredTag } from "./ConsentChoice";
import { PRIVACY_EMAIL, StatusCard } from "./JoinLayout";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString(dateLocale("en-GB"), {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

/**
 * The private link from a guardian's confirmation email (/v2/consent/:token):
 * see and change the consent choices for one child. Each change is recorded
 * as a new consent record by the API.
 */
export default function ManageConsent() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | invalid | expired | error
  const [choices, setChoices] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const show = (data) => {
    setPage(data);
    setChoices(data.claim.consents);
    setStatus("ready");
  };

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const { data } = await betaAPI.getGuardianConsent({ token });
      show(data);
    } catch (err) {
      console.error("Failed to load consent", err);
      setStatus(
        err.status === 404
          ? "invalid"
          : err.status === 410
          ? "expired"
          : "error"
      );
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  if (status === "loading") {
    return (
      <StatusCard>
        <div className="flex justify-center py-8">
          <Loader2 size={32} className="animate-spin text-[#E07A3A]" />
        </div>
      </StatusCard>
    );
  }

  if (status === "invalid" || status === "expired" || status === "error") {
    return (
      <StatusCard>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FEF2F2] text-[#DC2626]">
            <Link2Off size={22} />
          </div>
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {status === "invalid"
              ? t("auth.manageConsent.invalidTitle")
              : status === "expired"
              ? t("auth.manageConsent.expiredTitle")
              : t("auth.joinTeam.errorTitle")}
          </h1>
          <p className="mb-6 text-[15px] leading-relaxed text-[#6B7280]">
            {status === "invalid"
              ? t("auth.manageConsent.invalidBody", { email: PRIVACY_EMAIL })
              : status === "expired"
              ? t("auth.manageConsent.expiredBody", { email: PRIVACY_EMAIL })
              : t("auth.joinTeam.errorBody")}
          </p>
          {status === "error" && (
            <V2AuthButton variant="secondary" onClick={load}>
              {t("common.actions.tryAgain")}
            </V2AuthButton>
          )}
        </div>
      </StatusCard>
    );
  }

  const { claim, consent } = page;
  const current = claim.consents;
  const changed = Object.keys(current).some((k) => current[k] !== choices[k]);
  const withdrawingClub = current.club_tracking && !choices.club_tracking;

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const { data } = await betaAPI.updateGuardianConsent({
        token,
        body: { ...choices, consent_version: consent.version },
      });
      show(data);
      setSaved(true);
    } catch (err) {
      console.error("Failed to save consent", err);
      setSaveError(
        err.payload?.code === "consent_version_changed"
          ? t("auth.guardianSignup.errors.consent_version_changed")
          : t("auth.guardianSignup.errors.generic")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl rounded-2xl border border-[#EEF0F2] bg-white p-6 shadow-sm sm:p-10">
      <div className="mb-8 text-center">
        <p className="text-sm font-medium text-[#6B7280]">
          {claim.workspace_name} · {claim.team_name}
        </p>
        <h1 className="mt-1 text-2xl font-bold text-[#111827]">
          {t("auth.manageConsent.title", { child: claim.child_name })}
        </h1>
        <p className="mt-2 text-sm text-[#6B7280]">
          {t("auth.manageConsent.lastChanged", {
            date: formatDate(page.updated_at),
          })}
        </p>
      </div>

      <div className="space-y-3">
        {consent.choices.map((c) => (
          <ConsentChoice
            key={c.purpose}
            title={c.title}
            text={c.text}
            badge={<RequiredTag required={c.required} />}
            checked={!!choices[c.purpose]}
            onChange={(value) => {
              setSaved(false);
              setChoices((prev) => ({ ...prev, [c.purpose]: value }));
            }}
            disabled={saving}
          />
        ))}
      </div>

      {withdrawingClub && (
        <div className="mt-4 flex gap-3 rounded-xl border border-[#FDE68A] bg-[#FFFBEB] p-4">
          <AlertTriangle
            size={18}
            className="mt-0.5 flex-shrink-0 text-[#B45309]"
          />
          <p className="text-sm leading-relaxed text-[#92400E]">
            {t("auth.manageConsent.withdrawClub", { child: claim.child_name })}
          </p>
        </div>
      )}

      {saveError && (
        <div
          role="alert"
          className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
        >
          {saveError}
        </div>
      )}

      <div className="mt-6">
        {saved && !changed ? (
          <p className="flex items-center justify-center gap-2 py-3 text-[15px] font-medium text-[#047857]">
            <CheckCircle2 size={18} />
            {t("auth.manageConsent.saved")}
          </p>
        ) : (
          <V2AuthButton
            onClick={save}
            disabled={!changed}
            loading={saving}
            className="py-3.5 text-base"
          >
            {t("auth.manageConsent.save")}
          </V2AuthButton>
        )}
      </div>

      <p className="mt-6 border-t border-[#F3F4F6] pt-5 text-sm leading-relaxed text-[#6B7280]">
        {t("auth.manageConsent.requests", {
          child: claim.child_name,
          email: PRIVACY_EMAIL,
        })}
      </p>
    </div>
  );
}
